"use client";
import React, { useState, useEffect } from "react";
import "./ImageGallerySlider.scss";
type Slide = string | { src: string; caption?: string };

interface ImageSliderProps {
  slides: Slide[];
  caption?: string[];
  showCaption?: boolean;
  autoPlay?: boolean;
  interval?: number;
  slidesToShow?: number;
  slideClass?: string;
  slideId?: string;
  btnText?: string;
  btnLink?: string;
  onClick?: () => void;
}

const ImageGallerySlider: React.FC<ImageSliderProps> = ({
  slides,
  autoPlay = true,
  interval = 3000,
  slidesToShow = 3,
  slideClass,
  slideId,
  btnText,
  btnLink,
  onClick,
  caption,
  showCaption
}) => {
  // Normalize slides so we always have {src, caption}
  const normalizedSlides = slides.map((slide) =>
    typeof slide === "string" ? { src: slide } : slide
  );

  const extendedSlides = [
    ...normalizedSlides,
    ...normalizedSlides,
    ...normalizedSlides,
  ];
  const offset = normalizedSlides.length;

  const [currentIndex, setCurrentIndex] = useState(offset);
  const [paused, setPaused] = useState(false);
  const [transition, setTransition] = useState(true);

  const nextSlide = () => setCurrentIndex((i) => i + 1);
  const prevSlide = () => setCurrentIndex((i) => i - 1);

  // autoplay
  useEffect(() => {
    if (!autoPlay || normalizedSlides.length <= 1) return;
    const id = setInterval(() => {
      if (!paused) nextSlide();
    }, interval);
    return () => clearInterval(id);
  }, [autoPlay, interval, paused, normalizedSlides.length]);

  // reset when reaching edges
  useEffect(() => {
    if (currentIndex >= offset + normalizedSlides.length) {
      setTimeout(() => {
        setTransition(false);
        setCurrentIndex(offset);
      }, 600);
    }
    if (currentIndex < offset) {
      setTimeout(() => {
        setTransition(false);
        setCurrentIndex(offset + normalizedSlides.length - 1);
      }, 600);
    }
  }, [currentIndex, normalizedSlides.length, offset]);

  useEffect(() => {
    if (!transition) {
      const id = setTimeout(() => setTransition(true), 50);
      return () => clearTimeout(id);
    }
  }, [transition]);

  // active dot index
  const activeDot =
    (currentIndex - offset + normalizedSlides.length) % normalizedSlides.length;

  // go to specific slide via dots
  const goToSlide = (index: number) => {
    setCurrentIndex(offset + index);
  };
  if (slides.length === 0) return null;
  return (
    <div
      className={`mfg-slider-container${slideClass ? " " + slideClass : ""}`}
      id={slideId}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <button
        className="prev-button"
        onClick={prevSlide}
        aria-label="Previous slide"
      >
        ❮
      </button>

      <div className="slider-wrapper">
        <div
          className="slider-track"
          style={{
            transform: `translateX(-${(currentIndex * 100) / slidesToShow}%)`,
            transition: transition ? "transform 0.6s ease" : "none",
          }}
        >
          {extendedSlides.map((slide, i) => (
            <div
              key={i}
              className="slide"
              style={{ flex: `0 0 ${100 / slidesToShow}%` }}
            >
              <div className="slideInner">
                <img
                  src={slide.src}
                  alt={slide.caption ?? `Slide ${i + 1}`}
                  className="slider-image"
                />
                {caption && caption[i % caption.length] && showCaption && (
                  <div className="caption">{caption[i % caption.length]}</div>
                )}
                {btnLink ? (
                  <a
                    href={btnLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn"
                  >
                    {btnText}
                  </a>
                ) : (
                  btnText && (
                    <button className="btn" onClick={onClick}>
                      {btnText}
                    </button>
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <button
        className="next-button"
        onClick={nextSlide}
        aria-label="Next slide"
      >
        ❯
      </button>

      {/* Dots */}
      <div className="dots">
        {normalizedSlides.map((_, i) => (
          <button
            key={i}
            className={`dot ${activeDot === i ? "active" : ""}`}
            onClick={() => goToSlide(i)}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default ImageGallerySlider;
